import { omit } from 'lodash';
import { API_GATEWAY_URL } from '../constants/config';
import { withStandardHandlers } from './utils';
/**
 * Get all inspection sheets for an inspection area
 * @param accessToken - The access token for authentication.
 * @param projectId - The project ID.
 * @param inspectionAreaId - The inspection area ID.
 */
export const getInspectionSheets = async (accessToken, projectId, inspectionAreaId) => {
    const doFetch = async (allInspectionSheets, lastEvaluatedKey) => {
        const params = new URLSearchParams({ limit: '1000' });
        if (lastEvaluatedKey) {
            params.append('last_evaluated_key', JSON.stringify(lastEvaluatedKey));
        }
        const fn = () => {
            return fetch(`${API_GATEWAY_URL}/projects/${projectId}/inspection-areas/${inspectionAreaId}/inspection-sheets?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    responseType: 'json',
                    'X-Authorization': `Bearer ${accessToken}`,
                },
            });
        };
        const response = await withStandardHandlers(fn, 'errors.inspectionSheet.fetch', false)();
        allInspectionSheets.push(...response.results);
        if (response.last_evaluated_key) {
            return doFetch(allInspectionSheets, response.last_evaluated_key);
        }
        return allInspectionSheets;
    };
    return doFetch([]);
};
/**
 * Get a single inspection sheet by ID
 * @param accessToken - The access token for authentication.
 * @param projectId - The project ID.
 * @param inspectionAreaId - The inspection area ID.
 * @param inspectionSheetId - The inspection sheet ID.
 */
export const getInspectionSheet = (accessToken, projectId, inspectionAreaId, inspectionSheetId) => {
    const fn = () => fetch(`${API_GATEWAY_URL}/projects/${projectId}/inspection-areas/${inspectionAreaId}/inspection-sheets/${inspectionSheetId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
    });
    return withStandardHandlers(fn, 'errors.inspectionSheet.fetch')();
};
/**
 * Create a new inspection sheet
 * @param accessToken - The access token for authentication.
 * @param projectId - The project ID.
 * @param inspectionAreaId - The inspection area ID.
 * @param inspectionSheet - The inspection sheet details to create.
 */
export const postInspectionSheet = (accessToken, projectId, inspectionAreaId, inspectionSheet) => {
    const fn = () => fetch(`${API_GATEWAY_URL}/projects/${projectId}/inspection-areas/${inspectionAreaId}/inspection-sheets`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(omit(inspectionSheet, ['partition_key', 'inspection_area_id', 'inspection_sheet_id'])),
    });
    return withStandardHandlers(fn, 'errors.inspectionSheet.create')();
};
/**
 * Update an existing inspection sheet. Pass the **full fetched sheet** with the edits
 * applied — the endpoint validates fields beyond the editable ones (e.g. the criterion
 * values), so they must be echoed back; only the id/read-only keys are stripped.
 * @param accessToken - The access token for authentication.
 * @param projectId - The project ID.
 * @param inspectionAreaId - The inspection area ID.
 * @param inspectionSheetId - The inspection sheet ID to update.
 * @param inspectionSheet - The full sheet record with the edits applied.
 */
export const patchInspectionSheet = (accessToken, projectId, inspectionAreaId, inspectionSheetId, inspectionSheet) => {
    const fn = () => fetch(`${API_GATEWAY_URL}/projects/${projectId}/inspection-areas/${inspectionAreaId}/inspection-sheets/${inspectionSheetId}`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(omit(inspectionSheet, ['partition_key', 'inspection_area_id', 'inspection_sheet_id'])),
    });
    return withStandardHandlers(fn, 'errors.inspectionSheet.update')();
};
